import React, { useEffect, useId, useState } from "react";
import { Modal } from "react-bootstrap";
import { getApiEndpoint } from "../../../utils/constants/api";
import { wfetch } from "../../../utils/fetch";

// Authorable form component: fetches a dynamic-form definition from the forms REST API
// and renders it inline or behind an overlay trigger. The React sibling of the FTL
// feature-dynamic-form component — same GET/POST contract on /.rest/forms/v1/forms/{id},
// same payload shape ([{questionId, value}], one entry per selected multi value).
//
// The foundation knows no forms module: without one the definition fetch fails and the
// component renders nothing. Availability in page/grid areas and the form picker dialog
// field are wired up by the demo's feature-dynamic-form decorations.

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

const parseValidation = (question) => {
    const raw = question.content && question.content.validation;
    if (!raw) return {};
    try {
        return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (e) {
        return {};
    }
};

const TEXT_INPUT_TYPES = { email: "email", phone: "tel" };

const FormField = ({ question, value, setValue }) => {
    const validation = parseValidation(question);
    const required = !!validation.required;

    switch (question.questionType) {
        case "text": {
            const rows = validation.textRows || 1;
            const shared = {
                required,
                maxLength: validation.maxCharacters || undefined,
                className: "form-control",
                value: value || "",
                onChange: (e) => setValue(e.target.value),
            };

            if (rows > 1) return <textarea rows={rows} {...shared} />;

            return (
                <input
                    type={TEXT_INPUT_TYPES[validation.validatorType] || "text"}
                    pattern={validation.validatorType === "regex" && validation.regexPattern ? validation.regexPattern : undefined}
                    {...shared}
                />
            );
        }

        case "date":
            return <input type="date" required={required} className="form-control" value={value || ""} onChange={(e) => setValue(e.target.value)} />;

        case "boolean":
        case "single": {
            const options =
                question.questionType === "boolean"
                    ? [
                          { value: "yes", label: "yes" },
                          { value: "no", label: "no" },
                      ]
                    : sortByOrder(question.answerOptions);

            return (
                <div className="d-flex flex-wrap gap-4">
                    {options.map((option) => (
                        <label key={option.value} className="form-check-label d-flex align-items-center gap-2">
                            <input
                                type="radio"
                                className="form-check-input m-0"
                                name={`question_${question.id}`}
                                required={required}
                                checked={value === option.value}
                                onChange={() => setValue(option.value)}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            );
        }

        case "multi": {
            const maxSelections = validation.maxSelections || 0;
            const selected = Array.isArray(value) ? value : [];
            const toggle = (optionValue) =>
                setValue(selected.includes(optionValue) ? selected.filter((v) => v !== optionValue) : [...selected, optionValue]);

            return (
                <div className="d-flex flex-wrap gap-4">
                    {sortByOrder(question.answerOptions).map((option) => (
                        <label key={option.value} className="form-check-label d-flex align-items-center gap-2">
                            <input
                                type="checkbox"
                                className="form-check-input m-0"
                                checked={selected.includes(option.value)}
                                disabled={maxSelections > 0 && selected.length >= maxSelections && !selected.includes(option.value)}
                                onChange={() => toggle(option.value)}
                            />
                            {option.label}
                        </label>
                    ))}
                    {maxSelections > 0 && <small className="text-muted w-100">Max {maxSelections} selections</small>}
                </div>
            );
        }

        case "range": {
            const from = question.rangeFrom || 0;
            const to = question.rangeTo || 10;

            return (
                <div>
                    <input
                        type="range"
                        className="form-range"
                        min={from}
                        max={to}
                        value={value !== undefined ? value : from}
                        onChange={(e) => setValue(e.target.value)}
                    />
                    <div className="d-flex justify-content-between text-muted small">
                        <span>{from}</span>
                        <span>{to}</span>
                    </div>
                </div>
            );
        }

        case "fileUpload":
            // Files can't travel in the JSON payload; like the FTL renderer, only the
            // selected file's name is submitted.
            return <input type="file" required={required} className="form-control" onChange={(e) => setValue(e.target.files[0] ? e.target.files[0].name : "")} />;

        default:
            return null;
    }
};

const DynamicForm = ({ formId, displayMode, buttonLabel }) => {
    const [definition, setDefinition] = useState(null);
    const [values, setValues] = useState({});
    const [status, setStatus] = useState("idle");
    const [showOverlay, setShowOverlay] = useState(false);
    // In overlay mode the submit button sits in the modal footer, outside the <form>; the
    // HTML form attribute ties it back to the form by id (unique per component instance).
    const formDomId = useId();

    const endpoint = formId ? getApiEndpoint("FORMS", { pathParams: { formId: encodeURIComponent(formId) } }) : null;

    useEffect(() => {
        let cancelled = false;

        setDefinition(null);
        setValues({});
        setStatus("idle");

        if (!endpoint) return undefined;

        wfetch(endpoint).then((res) => {
            if (!cancelled && res && res.data) setDefinition(res.data);
        });

        return () => {
            cancelled = true;
        };
    }, [endpoint]);

    // No form id, no forms module, or an unresolvable definition: nothing to render.
    if (!definition) return null;

    const questions = sortByOrder(definition.questions);
    const submitText = (definition.content && definition.content.submit) || "Submit";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus("submitting");

        const payload = [];
        questions.forEach((q) => {
            const value = values[q.id];
            if (value === undefined || value === null || value === "") return;
            if (Array.isArray(value)) value.forEach((single) => payload.push({ questionId: q.id, value: single }));
            else payload.push({ questionId: q.id, value });
        });

        const res = await wfetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        setStatus(res ? "success" : "error");
    };

    const fields = (
        <React.Fragment>
            {definition.description && <p className="text-muted">{definition.description}</p>}

            {questions.map((question) => (
                <div className="mb-4" key={question.id}>
                    <label className="form-label fw-semibold">
                        {question.question}
                        {parseValidation(question).required && <span style={{ color: "#e74c3c" }}> *</span>}
                    </label>
                    <FormField question={question} value={values[question.id]} setValue={(v) => setValues({ ...values, [question.id]: v })} />
                </div>
            ))}
        </React.Fragment>
    );

    const submitButton = (
        <div className="btn-hover">
            <button type="submit" form={displayMode === "overlay" ? formDomId : undefined} className="default-btn" disabled={status === "submitting"}>
                {status === "submitting" ? "Submitting..." : submitText}
            </button>
        </div>
    );

    const errorMessage = status === "error" && (
        <div className="mt-3 w-100" style={{ color: "#e74c3c", fontSize: "12px" }}>
            Something went wrong submitting the form. Please try again.
        </div>
    );

    const successMessage = <div style={{ color: "#2ecc71" }}>Thank you! Your form has been submitted successfully.</div>;

    if (displayMode === "overlay") {
        return (
            <div>
                <div className="btn-hover">
                    <a
                        className="default-btn"
                        href="#form"
                        onClick={(e) => {
                            e.preventDefault();
                            setShowOverlay(true);
                        }}
                    >
                        {buttonLabel || definition.title || "Open form"}
                    </a>
                </div>

                {/* scrollable keeps the modal inside the viewport: header and footer stay put and only
                    the questions scroll, so the submit button is always visible without scrolling the
                    page (long forms used to push it below the fold). The class undoes flone's global
                    quick-view modal geometry, see _dynamic-form.scss. */}
                <Modal show={showOverlay} onHide={() => setShowOverlay(false)} className="dynamic-form-modal" centered scrollable>
                    <Modal.Header closeButton>
                        <Modal.Title as="h3" className="h5 mb-0">
                            {definition.title}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {status === "success" ? (
                            successMessage
                        ) : (
                            <form id={formDomId} onSubmit={handleSubmit}>
                                {fields}
                            </form>
                        )}
                    </Modal.Body>
                    {status !== "success" && (
                        <Modal.Footer className="justify-content-start">
                            {submitButton}
                            {errorMessage}
                        </Modal.Footer>
                    )}
                </Modal>
            </div>
        );
    }

    return (
        <div>
            {definition.title && <h2 className="mb-3">{definition.title}</h2>}
            {status === "success" ? (
                successMessage
            ) : (
                <form onSubmit={handleSubmit}>
                    {fields}
                    {submitButton}
                    {errorMessage}
                </form>
            )}
        </div>
    );
};

export default DynamicForm;